import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, Inject, ViewContainerRef } from '@angular/core';
import { ActivationStart, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BaseComponent, environment } from '@eview/core';
import { AuthHelpers } from '@eview/core/auth';
import { Permission } from '@eview/core/auth/permission';
import { ConfigHelpers } from '@eview/core/config/config.helpers';
import { SiteConfigItem } from '@eview/core/models/config';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@eview/core/models/user';
import { UserPermissions } from '@eview/core/models/user-permissions';
import { Logout } from '@eview/core/store/actions/auth.actions';
import { EConfigActions } from '@eview/core/store/actions/config.actions';
import { selectConfig } from '@eview/core/store/selectors/config.selector';
import { selectUserPermissions } from '@eview/core/store/selectors/user-permissions.selector';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { selectUnreadNotifications } from '@eview/core/store/selectors/notifications.selector';
import { selectNetworkStatus } from '@eview/core/store/selectors/network-status.selector';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PostEditorPageComponent } from '../shared/post-editor/post-editor/post-editor-page.component';
import { ToastService } from '../toast/toast.service';
import { ToastType } from '../toast/toast.component';
import { selectForms } from '@eview/core/store/selectors/forms.selector';
import { Form, sortFormsDefaultFirst } from '@eview/core/domain/form/form';
import { PostStatus, PostType } from '@eview/core/domain/post/post';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { selectTenant } from '@eview/core/store/selectors/tenant.selector';
import { selectCategories } from '@eview/core/store/selectors/tags.selector';
import { PostService } from '@eview/core/domain/post/post.service';
import { SetMapMarkers } from '@eview/core/store/actions/map.actions';
import { FeatureHelpers } from '@eview/core/domain/post/feature.helpers';
import { GetPostsData } from '@eview/core/store/actions/posts.actions';
import { PostHelpers } from '@eview/core/domain/post/post.helpers';

@Component({
  selector: 'eview-index',
  templateUrl: 'index.component.html',
  styleUrls: ['index.component.scss']
})
export class IndexComponent extends BaseComponent implements OnInit, OnDestroy {

  unreadNotifications$: Observable<boolean>
  isNetworkAvailable$: Observable<any>
  userPermissions$: Observable<UserPermissions>
  user$: Observable<User>
  footer$: Observable<string>
  deploymentName$: Observable<string>
  siteConfig$: Observable<SiteConfigItem>
  store: Store<AppState>
  time = { hour: 'Hrs', minute: 'Mins' };
  countryCodes = environment.countryCodes;
  currentLang: string;

  AuthHelpers = AuthHelpers;

  Permission = Permission;
  formList: Form[];
  PostType = PostType;

  pageInfo: any = { logo: { header: false, sidebar: true } };
  PostHelpers = PostHelpers;
  tenantName: string;
  tenantLogo: string;
  reportName: string;
  showNotifications: boolean = true;
  showSidebar: boolean = false;
  currentUser: any;
  tenant: any;
  openedAccordions: string[] = [];
  openedDesktopNavMenus: string[] = [];
  showDropdown: boolean = false;

  constructor(
    store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    private translate: TranslateService,
    private modalService: NgbModal,
    private spinner: SpinnerVisibilityService,
    private postService: PostService,
    private toast: ToastService,
    @Inject(ViewContainerRef) private viewContainerRef: ViewContainerRef
  ) {
    super();

    this.store = store
    this.currentLang = this.translate.currentLang;
    this.subs = new Subscription();
    this.configReady$ = this.actions$.pipe(
      ofType(EConfigActions.GetConfigSuccess),
      map(() => true)
    );
    toast.setViewContainerRef(viewContainerRef);

    this.subs.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.closeDesktopNavMenus();
          this.syncMobileAccordionsForActiveRoute();
        })
    );

    this.siteConfig$ = this.store
      .select(selectConfig)
      .pipe(
        map(config =>
          ConfigHelpers.GetConfigItem<SiteConfigItem>(SiteConfigItem, config)
        )
      );

    this.deploymentName$ = this.siteConfig$.pipe(
      map(siteConfig =>
        siteConfig ? siteConfig.name : environment.deploymentName
      )
    );

    this.footer$ = this.siteConfig$.pipe(
      map(siteConfig =>
        siteConfig ? siteConfig.description : environment.footer.text
      )
    );
    this.user$ = this.store.select(selectUser);
    this.userPermissions$ = this.store.select(
      selectUserPermissions
    );
    this.isNetworkAvailable$ = this.store.select(
      selectNetworkStatus
    );
    this.unreadNotifications$ = this.store.select(
      selectUnreadNotifications
    );

  }



  ngOnInit() {
    
    this.subs.add(
      // to listen for route changes
      this.router.events.subscribe(e => {
        if (e instanceof ActivationStart) {
          window.scroll(0, 0);
        }

        if (this.showDropdown) {
          this.showDropdown = false;
        }


      })
    );

    this.subs.add(
      combineLatest([
        this.store.select(selectForms),
        this.store.select(selectUserPermissions)
      ]).subscribe(([forms, userPermissions]) => {
            let reportNavigation = []
            if (forms) { // forms will be null if the api call fails
              const viewIds = userPermissions
                ? AuthHelpers.User.accessibleFormIdsSync(userPermissions, 'view')
                : [];
              const createIds = userPermissions
                ? AuthHelpers.User.accessibleFormIdsSync(userPermissions, 'create')
                : [];
              // Show form in menu if view OR create (create-only roles still reach forms)
              const allowed = (formId: number) => {
                if (viewIds === null || createIds === null) {
                  return true; // all forms
                }
                if (!userPermissions) {
                  return false;
                }
                if (!viewIds.length && !createIds.length) {
                  // no grant rows: legacy dual-read may still allow via canFormSync
                  return (
                    AuthHelpers.User.canFormSync(userPermissions, formId, 'view') ||
                    AuthHelpers.User.canFormSync(userPermissions, formId, 'create')
                  );
                }
                return viewIds.includes(formId) || createIds.includes(formId);
              };
              this.formList = sortFormsDefaultFirst(
                forms.results.filter(
                  form =>
                    !form.disabled &&
                    this.formNavMappableForMenu(form) &&
                    allowed(form.id)
                )
              );
              reportNavigation = this.formList.map(obj => ({
                link: `/dashboard/posts/${this.postPathSegmentForForm(
                  obj
                )}/${obj.id}`,
                icon: '',
                text: PostHelpers.GetReportLabelName(obj.name),
                // Already filtered by grants above — avoid re-check that drops create-only forms.
                formId: obj.id
              }));
            }
            else {
              this.formList = []
            }

            this.navbarUserItems = [{
              link: '/dashboard/map',
              icon: '',
              text: 'Home',
            },
            {
              link: '/dashboard/posts',
              icon: '',
              text: 'Reports',
              children: reportNavigation
            },
            {
              link: '/dashboard/posts/resumen',
              icon: '',
              text: 'MENU_STATISTICS',
              formAction: 'analysis' as const,
              formId: null,
              children: [
              {
                link: '/dashboard/posts/resumen',
                icon: '',
                text: 'MENU_INCIDENTS_SUMMARY',
                formAction: 'analysis' as const,
                formId: null
              }]
            },
            {
              link: '/dashboard/categories',
              icon: '',
              text: 'MENU_REPORT_SETUP',
              // Parent visible if any child is allowed (tags OR forms).
              permission: [
                Permission.AccessAndEditIncidentIndicators,
                Permission.TagsManage,
                Permission.AccessAndEditIncidentReportsFormNamesOfDeports,
                Permission.FormsConfigure
              ],
              children: [{
                link: '/dashboard/categories/tags',
                icon: '',
                text: 'MENU_TAGS',
                permission: [
                  Permission.AccessAndEditIncidentIndicators,
                  Permission.TagsManage
                ]
              },
              {
                link: '/dashboard/categories/forms-management',
                icon: '',
                text: 'MENU_REPORT_FORMS',
                permission: [
                  Permission.AccessAndEditIncidentReportsFormNamesOfDeports,
                  Permission.FormsConfigure
                ]
              }]
            },
            {
              link: '/dashboard/settings/',
              icon: '',
              text: 'MENU_SETTINGS',
              permission: [
                Permission.AccessManagement,
                Permission.RolesManage,
                Permission.UserManagement,
                Permission.UsersManage
              ],
              children: [{
                link: '/dashboard/settings/user-access-management',
                icon: '',
                text: 'ACCESS_MGMT',
                permission: [
                  Permission.AccessManagement,
                  Permission.RolesManage
                ]
              },
              {
                link: '/dashboard/settings/user-management',
                icon: '',
                text: 'USER_MANAGEMENT',
                permission: [
                  Permission.UserManagement,
                  Permission.UsersManage
                ]
              }]
            }]
          }
        ));

    this.subs.add(
      this.user$.subscribe((res) => {
        this.currentUser = res;
      })
    );


    this.subs.add(
      combineLatest([
        this.store.select(selectTenant),
        this.store.select(selectUser)
      ]).subscribe(([tenant, user]) => {
        if (!tenant?.unique_name) {
          return;
        }
        this.tenant = tenant;
        this.tenantName = tenant.unique_name;
        this.reportName = tenant.report_name;
        this.tenantLogo = 'assets/' + tenant.unique_name + '/' + 'logo.png';
        if (this.tenantName === 'madagascar' || this.tenantName === 'peru') {
          if (user) {
            this.showNotifications = true;
            this.showSidebar = false;
          } else {
            this.showNotifications = false;
            this.showSidebar = true;
          }
        }
        this.rebuildAnonymousNavbar(user);
      })
    );



  }

  /** Menú superior anónimo (como antes: sin Consulta MACE ni Informes). */
  private rebuildAnonymousNavbar(user: User | null) {
    if (user) {
      return;
    }
    this.navbarAnonymousItems = [
      { link: '/dashboard/map', icon: '', text: 'Home' },
      { link: '/dashboard/about-us', icon: '', text: 'ABOUT_US_HEADER' },
      { link: '/dashboard/get-alerts', icon: '', text: 'GET_ALERTS' },
      { link: '/dashboard/contact-us', icon: '', text: 'CONTACT_US' }
    ];
  }

  /** Misma regla que el rail del mapa (`mapSwitcherMappableFormType`). */
  private formNavMappableForMenu(form: Form): boolean {
    const t = form.type;
    if (t == null || String(t).trim() === '') {
      return true;
    }
    const s = String(t);
    return (
      s === PostType.Incident ||
      s === PostType.Risk ||
      s === 'incident' ||
      s === 'risk' ||
      s === 'report'
    );
  }

  private postPathSegmentForForm(form: Form): string {
    if (form.type === PostType.Risk || form.type === 'risk') {
      return 'risk';
    }
    if (form.type === PostType.Incident || form.type === 'incident') {
      return 'incident';
    }
    if (form.type === 'mace') {
      return 'mace';
    }
    if (form.type === 'report') {
      return 'report';
    }
    return 'incident';
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  @ViewChild(RouterOutlet, { static: false }) outlet: RouterOutlet;

  private subs: Subscription;

  configReady$: Observable<boolean>;


  footer = {
    ...environment.footer,
    year: new Date().getFullYear()
  };

  sidebarItems: {
    link: string;
    icon: string;
    text: string;
    permission?: Permission;
    children?: {
      link: string;
      icon: string;
      text: string;
      permission?: Permission;
    }[];
  }[] = [
      { link: '/dashboard/map', icon: '', text: 'MENU_MAP_VIEW', permission: Permission.AccessIncidentRiskReports },
      { link: '/dashboard/map/lern', icon: '', text: 'MENU_LERN_MAP_VIEW', permission: Permission.AccessIncidentRiskReports },
      // { link: '/dashboard/map', icon: '', text: 'MENU_MAP_VIEW', permission: Permission.AccessEVIEWDashboard },
      // { link: '/dashboard/map/lern', icon: '', text: 'MENU_LERN_MAP_VIEW', permission: Permission.AccessEVIEWDashboard },
      {
        link: '/dashboard/posts/lern',
        icon: '',
        text: 'MENU_LERN_LIST_VIEW',
        permission: Permission.AccessIncidentRiskReports,
      },
      {
        link: '/dashboard/posts/incident',
        icon: '',
        text: 'MENU_POSTS',
        permission: Permission.AccessIncidentRiskReports,
      },
      {
        link: '/dashboard/posts/risk',
        icon: '',
        text: 'MENU_RISK_REPORTS',
        permission: Permission.AccessIncidentRiskReports,
      },
      {
        link: '/dashboard/analysis',
        icon: '',
        text: 'MENU_ANALYSIS',
        permission: Permission.AccessAnalysisAndFiltersSection
      },
      {
        link: '/dashboard/analysis-templates',
        icon: '',
        text: 'MENU_ANALYSIS_FILTERS',
        permission: Permission.AccessAnalysisAndFiltersSection
      },
      {
        link: '/dashboard/forms-management',
        icon: '',
        text: 'FORMS_MANAGEMENT',
        permission: Permission.AccessAndModifyParametersSettings
      },
      {
        link: '/dashboard/tags',
        icon: '',
        text: 'MENU_TAGS',
        permission: Permission.AccessAndModifyParametersSettings
      },
      {
        link: '/dashboard/user-access-management',
        icon: '',
        text: 'ACCESS_MGMT',
        permission: Permission.AccessAndModifyParametersSettings
      },
      {
        link: '/dashboard/user-management',
        icon: '',
        text: 'USER_MANAGEMENT',
        permission: Permission.AccessAndModifyParametersSettings
      }
    ];

  sidebarItemsSecondGroup: {
    link: string;
    icon: string;
    text: string;
    permission?: Permission;
    children?: {
      link: string;
      icon: string;
      text: string;
      permission?: Permission;
    }[];
  }[] = [
    ];

  menuVisibile: boolean = true;



  sidebarIreportItems: {
    link: string;
    icon: string;
    text: string;
    slug?: string;

    children?: {
      link: string;
      icon: string;
      text: string;

    }[];
  }[] = [
      { link: '/dashboard/map', icon: '', text: 'MENU_MAP_VIEW' },
      { link: '/dashboard/map/lern', icon: '', text: 'MENU_LERN_MAP_VIEW' },
      { link: '/dashboard/posts/incident', icon: '', text: 'MENU_LIST_VIEW' },
      { link: '/dashboard/get-alerts', icon: '', text: 'MENU_GET_ALERTS', slug: 'get-alerts' },
      { link: '/dashboard/contact-us', icon: '', text: 'MENU_CONTACT_US', slug: 'contact-us' },
    ];

  navbarAnonymousItems: {
    link: string;
    icon: string;
    text: string;
    permission?: Permission;
    children?: {
      link: string;
      icon: string;
      text: string;
      textIsRaw?: boolean;
      permission?: Permission;
    }[];
  }[] = [
      { link: '/dashboard/map', icon: '', text: 'Home' },
      { link: '/dashboard/about-us', icon: '', text: 'ABOUT_US_HEADER' },
      { link: '/dashboard/get-alerts', icon: '', text: 'GET_ALERTS' },
      { link: '/dashboard/contact-us', icon: '', text: 'CONTACT_US' }
    ];

  navbarUserItems: {
    link: string;
    icon: string;
    text: string;
    permission?: Permission | Permission[];
    formId?: number | null;
    formAction?: string;
    children?: {
      link: string;
      icon: string;
      text: string;
      permission?: Permission | Permission[];
      formId?: number | null;
      formAction?: string;
    }[];
  }[] = [
      {
        link: '/',
        icon: '',
        text: 'Home',
        // permission: Permission.AccessEVIEWDashboard
        permission: Permission.AccessIncidentRiskReports
      },
      {
        link: '',
        icon: '',
        text: 'Reports',
        children: [{
          link: '/dashboard/posts/incident',
          icon: '',
          text: 'MENU_POSTS',
          permission: Permission.AccessIncidentRiskReports
        },
        // {
        //   link: '/dashboard/posts/risk',
        //   icon: '',
        //   text: 'Risk Reports'
        // },
        {
          link: '/dashboard/analysis',
          icon: '',
          text: 'MENU_ANALYSIS',
          permission: Permission.AccessAnalysisAndFiltersSection
        },
        {
          link: '/dashboard/analysis-templates',
          icon: '',
          text: 'MENU_ANALYSIS_FILTERS',
          permission: Permission.AccessAnalysisAndFiltersSection
        },
        ]
      },
      {
        link: '',
        icon: '',
        text: 'MENU_REPORT_SETUP',
        permission: Permission.AccessAndModifyParametersSettings,
        children: [{
          link: '/dashboard/tags',
          icon: '',
          text: 'MENU_TAGS'
        },
        {
          link: '/dashboard/forms-management',
          icon: '',
          text: 'MENU_REPORT_FORMS'
        }]
      },
      {
        link: '',
        icon: '',
        text: 'MENU_SETTINGS',
        permission: Permission.AccessAndModifyParametersSettings,
        children: [{
          link: '/dashboard/user-access-management',
          icon: '',
          text: 'ACCESS_MGMT'
        },
        {
          link: '/dashboard/user-management',
          icon: '',
          text: 'USER_MANAGEMENT'
        }]
      }
    ];

  hasUserPermission(permission: Permission | Permission[]) {
    return AuthHelpers.User.HasUserPermission(this.store, permission);
  }

  /**
   * Menu visibility: prefer form grants when formId/formAction set;
   * also accept legacy permission strings.
   */
  hasNavPermission(item: {
    permission?: Permission | Permission[];
    formId?: number | null;
    formAction?: string;
  }) {
    if (item && item.formAction) {
      return AuthHelpers.User.CanForm(
        this.store,
        item.formId != null ? Number(item.formId) : null,
        item.formAction as any
      );
    }
    if (item && item.permission) {
      return this.hasUserPermission(item.permission);
    }
    return of(true);
  }

  navItemHasChildren(item: { children?: unknown[] }): boolean {
    return !!item.children?.length;
  }

  /** Parent items with children are toggles only — no route navigation. */
  parentNavLink(item: { link?: string; children?: unknown[] }): string | null {
    if (this.navItemHasChildren(item)) {
      return null;
    }
    return item.link && item.link !== '' ? item.link : null;
  }

  isDesktopNavMenuOpen(menuKey: string): boolean {
    return this.openedDesktopNavMenus.includes(menuKey);
  }

  onParentNavClick(
    item: { text: string; children?: unknown[] },
    event: Event
  ): void {
    if (this.navItemHasChildren(item)) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleDesktopNavMenu(item.text);
      return;
    }
    this.closeDesktopNavMenus();
  }

  toggleDesktopNavMenu(menuKey: string): void {
    if (this.openedDesktopNavMenus.includes(menuKey)) {
      this.openedDesktopNavMenus = this.openedDesktopNavMenus.filter(
        key => key !== menuKey
      );
    } else {
      this.openedDesktopNavMenus = [menuKey];
    }
  }

  closeDesktopNavMenus(): void {
    this.openedDesktopNavMenus = [];
  }

  onSubmenuNavClick(event: Event): void {
    event.stopPropagation();
    this.closeDesktopNavMenus();
    this.closeNavDropdown();
  }

  isNavGroupActive(item: {
    link?: string;
    children?: { link: string }[];
  }): boolean {
    const url = this.normalizeNavPath(this.router.url);
    const children = item.children ?? [];

    // Parents with submenus: match only child routes (avoids /dashboard/posts
    // highlighting Informes when the URL is /dashboard/posts/analysis).
    if (children.length > 0) {
      return children.some(
        child => child.link && this.isNavPathActive(child.link, url)
      );
    }

    if (item.link && item.link !== '') {
      return this.isNavPathActive(item.link, url);
    }

    return false;
  }

  private normalizeNavPath(path: string): string {
    const base = (path || '').split('?')[0].split('#')[0];
    return base.replace(/\/$/, '') || '/';
  }

  private isNavPathActive(routePath: string, currentUrl: string): boolean {
    const route = this.normalizeNavPath(routePath);
    const url = this.normalizeNavPath(currentUrl);
    return url === route || url.startsWith(route + '/');
  }

  private syncMobileAccordionsForActiveRoute(): void {
    const items = [...this.navbarUserItems, ...this.navbarAnonymousItems];
    const toOpen = items
      .filter(item => item.children?.length && this.isNavGroupActive(item))
      .map(item => item.text);
    if (!toOpen.length) {
      return;
    }
    this.openedAccordions = [
      ...new Set([...this.openedAccordions, ...toOpen])
    ];
  }

  onLogoutClick() {
    this.store.dispatch(new Logout());
    this.store.select(selectCategories).subscribe(data => {
      if (data.length) {
        let tagsArray = [];
        data.forEach(tag => tagsArray.push(tag.id))
        const options = {
          status: PostStatus.All,
          has_location: 'mapped',
          parent: 'none',
          multiple_tags: tagsArray,
          type: 'report'
        };
        combineLatest(
          this.postService
            .geoJson(options),
          this.store.select(selectCategories)
        ).subscribe(([featureCollection, categories]) => {
          featureCollection.features.map(featureData => {
            if (featureData.properties.tags.length) {
              let tagID = featureData.properties.tags[0];
              let tagColorInCategories = categories.find(item => item.id === parseInt(tagID));
              typeof (tagColorInCategories) !== 'undefined' ? featureData.properties.color = tagColorInCategories.color : null
            }

          })
          this.store.dispatch(
            new SetMapMarkers({
              markers: FeatureHelpers.ToMarkers(featureCollection)
            })
          );
        });
        const payload = {
          status: PostStatus.All,
          offset: 0,
          limit: 10,
          parent: null,
          form: 1,
          type: 'report'
        };

        this.store.dispatch(new GetPostsData(payload));
      }
    }
    )
    //  this.router.navigateByUrl('/login2')
  }

  onLanguageChange(languageCode) {
    if (languageCode && languageCode.length > 0) {
      this.currentLang = languageCode;
      this.translate.use(this.currentLang);
      localStorage.setItem('languageCode', languageCode);
    }
  }

  //function to open modal popup
  openCreateIncident(type) {
    let formData;
    if (this.formList && this.formList.length > 0) {
      formData = this.formList.filter(item => item.type === type);
    }
    const createIncidentModalRef = this.modalService.open(PostEditorPageComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'customModalWindowClass post-editor-modal-window',
      size: 'xl',
      backdrop: 'static'
    });
    this.subs.add(
      createIncidentModalRef.componentInstance.modalCloseEvent.subscribe(($e) => {
        if ($e === 'success') {
          // Create success notification
          const notification = document.createElement('div');
          notification.innerHTML = '✓ Informe creado exitosamente';
          notification.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#28a745;color:white;padding:15px 20px;border-radius:8px;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 4px 6px rgba(0,0,0,0.1);animation:slideIn 0.3s ease-out';
          document.body.appendChild(notification);
          setTimeout(() => {
            notification.style.animation = 'slideOut 0.3s ease-in';
            setTimeout(() => document.body.removeChild(notification), 300);
          }, 3000);
        }
        if (createIncidentModalRef.componentInstance) {
          createIncidentModalRef.dismiss();
        }
      })
    );
    const initialFormId = (formData && formData[0]) ? formData[0]['id'] : 1;
    createIncidentModalRef.componentInstance.formId = initialFormId;
    createIncidentModalRef.componentInstance.type = type;
    createIncidentModalRef.componentInstance.initModalEditor();
  }


  login() {

    if (this.tenantName == 'madagascar' || this.tenantName == 'peru') {
      this.router.navigate(['/login2']);
    } else {
      this.router.navigate(['/login']);
    }

  }

  routeTo() {
    this.router.navigate(['/dashboard/map']);
  }

  handleOpenedAccordion(accordionName: string) {
    if (this.openedAccordions.includes(accordionName)){
      this.openedAccordions = this.openedAccordions.filter((tempAccordion: string) => tempAccordion !== accordionName)
    } else {
      this.openedAccordions.push(accordionName)
    }

  }

  checkOpenedAccordion(accordionName: string) {
    return this.openedAccordions.includes(accordionName) ? true : false
  }

  closeNavDropdown() {
    if (this.showDropdown) {
      this.showDropdown = false
    }
  }

  handleNavDropdown(event: Event) {
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
  }
  openDropdown(dropdownEvent) {
    if (this.tenantName === 'liberia' || this.tenantName == 'peru') {
      this.openCreateIncident(PostType.Incident)
    }
    else {
      dropdownEvent.open()
    }
  }
}
